import { createContext, type ReactNode, use } from "react";
import { useStore } from "zustand";

import type { UiStore, UiStoreApi } from "@mountayaapp/core";

/**
 * UiStoreContext holds the render-free studio UI store instance. The provider
 * lives here (a sanctioned JSX carve-out) while the store itself stays in
 * @mountayaapp/core, so the tool bus (ctx.ui) and the React tree mutate and read
 * the very same instance.
 */
const UiStoreContext = createContext<UiStoreApi | null>(null);

/**
 * UiStoreProviderProps inject the single studio UI store instance to expose. The
 * SPA passes the store it also threads through the tool context, so components
 * read the same store that tools mutate.
 */
interface UiStoreProviderProps {
  children: ReactNode;
  store: UiStoreApi;
}

/**
 * UiStoreProvider exposes a studio UI store via context. Components read it with
 * useUiStore; tools mutate the same instance through ctx.ui — the single
 * mutation surface for both humans and the agent.
 */
export const UiStoreProvider = ({ children, store }: UiStoreProviderProps) => (
  <UiStoreContext value={store}>{children}</UiStoreContext>
);

/**
 * useUiStore selects from the studio UI store with the given selector (pass
 * useShallow for multi-field selections). Uses the new use(Context) API (never
 * useContext) per the project convention. Throws when called outside a
 * UiStoreProvider.
 */
export const useUiStore = <T,>(selector: (state: UiStore) => T): T => {
  const store = use(UiStoreContext);
  if (!store) {
    throw new Error("useUiStore must be used within a UiStoreProvider");
  }

  return useStore(store, selector);
};

/**
 * useUiStoreApi returns the raw render-free studio UI store handle from context —
 * the same instance the tool bus mutates through ctx.ui. It is the read-side seam a
 * view needs to subscribe imperatively to a high-frequency slice without
 * re-rendering: the profile overlay redraws on the wizard's per-frame progress
 * fraction by subscribing to this handle, so a preview flight advancing
 * frame-by-frame repaints the canvas without re-rendering the React tree. Throws
 * when called outside a UiStoreProvider. Components must not mutate the store
 * through this handle — the single-mutation-surface invariant still routes writes
 * through the tool bus; this exists only for read-side imperative subscriptions.
 */
export const useUiStoreApi = (): UiStoreApi => {
  const store = use(UiStoreContext);
  if (!store) {
    throw new Error("useUiStoreApi must be used within a UiStoreProvider");
  }

  return store;
};
